import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Search,
  Plus,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { TABLE_HEADER } from '@/components/ui/pageChrome';
import { useTriggerReconciliation } from '@/hooks/usePayReconciliation';

interface Clinic {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  created_at: string;
  patient_count: number;
  stripe_status: string | null;
}

export function AdminClinics() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const triggerReconciliation = useTriggerReconciliation();
  const [refreshingClinicId, setRefreshingClinicId] = useState<string | null>(null);

  const { data: clinics, isLoading } = useQuery({
    queryKey: ['admin-clinics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinics')
        .select(
          `
 id,
 name,
 email,
 phone,
 is_active,
 created_at,
 legacy_clinic_stripe_accounts(status)
 `,
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Active patients only, matching list_sub_clinics. Counted in memory
      // the same way AdminUsers counts clinic memberships.
      const clinicIds = (data ?? []).map((c) => c.id);
      const countMap = new Map<string, number>();
      if (clinicIds.length > 0) {
        const { data: patientRows, error: patientError } = await supabase
          .from('patients')
          .select('clinic_id')
          .eq('status', 'active')
          .in('clinic_id', clinicIds);
        if (patientError) throw patientError;
        for (const row of patientRows ?? []) {
          countMap.set(row.clinic_id, (countMap.get(row.clinic_id) ?? 0) + 1);
        }
      }

      return (data ?? []).map((c) => ({
        ...c,
        patient_count: countMap.get(c.id) ?? 0,
        stripe_status:
          (c.legacy_clinic_stripe_accounts as { status: string }[] | null)?.[0]?.status ?? null,
      })) as Clinic[];
    },
  });

  const filteredClinics = clinics?.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <PageLayout
      title={t('admin.clinics.title')}
      subtitle={t('admin.clinics.subtitle')}
      actions={
        <Button disabled>
          <Plus className="mr-2 h-4 w-4" />
          {t('admin.clinics.add_clinic')}
        </Button>
      }
    >

      {/* Search */}
      <div className="mb-6 flex items-center gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder={t('admin.clinics.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leadingIcon={<Search className="h-4 w-4" aria-hidden />}
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-ds-line bg-ds-card">
          <table className="min-w-full divide-y divide-ds-line">
            <thead className="bg-ds-sunk">
              <tr>
                <th className={`px-6 py-3 text-left uppercase tracking-wider ${TABLE_HEADER}`}>
                  {t('admin.clinics.col_clinic')}
                </th>
                <th className={`px-6 py-3 text-left uppercase tracking-wider ${TABLE_HEADER}`}>
                  {t('admin.clinics.col_contact')}
                </th>
                <th className={`px-6 py-3 text-left uppercase tracking-wider ${TABLE_HEADER}`}>
                  {t('admin.clinics.col_status')}
                </th>
                <th className={`px-6 py-3 text-left uppercase tracking-wider ${TABLE_HEADER}`}>
                  {t('admin.clinics.col_stripe')}
                </th>
                <th className={`px-6 py-3 text-left uppercase tracking-wider ${TABLE_HEADER}`}>
                  {t('admin.clinics.col_patients')}
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ds-line">
              {filteredClinics?.map((clinic) => (
                <tr key={clinic.id} className="hover:bg-ds-row-hover">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-teal-tint">
                        <Building2 className="h-5 w-5 text-ds-teal" />
                      </div>
                      <div>
                        <p className="font-medium text-ds-text">{clinic.name}</p>
                        <p className="text-xs text-ds-muted">
                          {new Date(clinic.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-ds-muted">
                    <div>{clinic.email ?? '-'}</div>
                    <div>{clinic.phone ?? '-'}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {clinic.is_active ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ds-green-tint px-2 py-1 text-xs font-medium text-ds-green-ink">
                        <CheckCircle className="h-3 w-3" />
                        {t('admin.clinics.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ds-red-tint px-2 py-1 text-xs font-medium text-ds-red-ink">
                        <XCircle className="h-3 w-3" />
                        {t('admin.clinics.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {clinic.stripe_status === 'enabled' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ds-green-tint px-2 py-1 text-xs font-medium text-ds-green-ink">
                        <CheckCircle className="h-3 w-3" />
                        {t('admin.clinics.stripe_enabled')}
                      </span>
                    ) : clinic.stripe_status ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-ds-amber-tint px-2 py-1 text-xs font-medium text-ds-amber-ink">
                        {clinic.stripe_status}
                      </span>
                    ) : (
                      <span className="text-xs text-ds-muted">-</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-ds-text">
                    {clinic.patient_count}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm">
                    <div className="flex items-center justify-end gap-3">
                      <Button
                        type="button"
                        variant="link"
                        title="Refresh Pay status"
                        onClick={() => {
                          setRefreshingClinicId(clinic.id);
                          triggerReconciliation.mutate(
                            { clinic_id: clinic.id },
                            {
                              onSettled: () => setRefreshingClinicId(null),
                            },
                          );
                        }}
                        disabled={refreshingClinicId === clinic.id}
                      >
                        {refreshingClinicId === clinic.id ? (
                          <Loader2 className="animate-spin" />
                        ) : (
                          <RefreshCw />
                        )}
                        <span>Pay</span>
                      </Button>
                      <ButtonLink to={`/settings/clinics/${clinic.id}`} variant="link">
                        {t('admin.clinics.view')}
                        <ExternalLink />
                      </ButtonLink>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredClinics?.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-ds-muted">
                    {t('admin.clinics.no_clinics')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-ds-line bg-ds-card p-4">
          <p className="text-sm text-ds-muted">{t('admin.clinics.total_clinics')}</p>
          <p className="mt-1 text-2xl font-bold text-ds-text">{clinics?.length ?? 0}</p>
        </div>
        <div className="rounded-lg border border-ds-line bg-ds-card p-4">
          <p className="text-sm text-ds-muted">{t('admin.clinics.active_clinics')}</p>
          <p className="mt-1 text-2xl font-bold text-ds-green">
            {clinics?.filter((c) => c.is_active).length ?? 0}
          </p>
        </div>
        <div className="rounded-lg border border-ds-line bg-ds-card p-4">
          <p className="text-sm text-ds-muted">{t('admin.clinics.stripe_connected')}</p>
          <p className="mt-1 text-2xl font-bold text-ds-teal">
            {clinics?.filter((c) => c.stripe_status === 'enabled').length ?? 0}
          </p>
        </div>
      </div>
    </PageLayout>
  );
}
